import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../components/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, X, ArrowLeft } from 'lucide-react';

const PermissionPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [allPermissions, setAllPermissions] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [userData, setUserData] = useState(null);

  // Composant Toast pour les erreurs
  const ErrorToast = ({ message, onClose }) => (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-lg flex items-center justify-between">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
          <span className="text-red-700">{message}</span>
        </div>
        <button onClick={onClose} className="text-red-500 hover:text-red-700">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [permissionsResponse, userPermissionsResponse] = await Promise.all([
          api.get('/utilisateur/permissions/'),
          api.get(`/utilisateur/${userId}/permissions/`)
        ]);

        setAllPermissions(permissionsResponse.data);
        setUserPermissions(userPermissionsResponse.data.permissions);
        setSelectedPermissions(userPermissionsResponse.data.permissions.map(p => p.id));

      } catch (err) {
        setError(err.response?.data?.message || "Une erreur est survenue");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const handlePermissionChange = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await api.post(`/utilisateur/${userId}/permissions/update/`, {
        permissions: selectedPermissions
      });
      navigate('/utilisateurs');
    } catch (err) {
      setError(err.response?.data?.message || "Une erreur est survenue lors de la mise à jour des permissions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <Loader2 className="h-8 w-8 text-emerald-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* En-tête */}
          <div className="mb-8">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/utilisateurs')}
                className="mr-4 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">
                Gestion des permissions
              </h1>
            </div>
          </div>

          {error && <ErrorToast message={error} onClose={() => setError(null)} />}

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              {Object.entries(allPermissions).map(([modelName, permissions]) => (
                <div key={modelName} className="mb-8">
                  <h2 className="text-lg font-medium text-gray-900 mb-4 capitalize">
                    Permissions {modelName}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {permissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="relative flex items-start"
                      >
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => handlePermissionChange(permission.id)}
                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <span className="font-medium text-gray-700">
                            {permission.name}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer les modifications'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PermissionPage;
